import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, CheckCircle2, AlertCircle, RefreshCw, Trash2, Sun, Aperture } from 'lucide-react';
import { checkImageQuality } from '@/lib/kiosk-image-quality';

type FotoTag = 'Front' | 'Heck' | 'Seite links' | 'Seite rechts' | 'Detail' | 'Innenraum' | 'Sonstiges';

const FOTO_TAGS: FotoTag[] = ['Front', 'Heck', 'Seite links', 'Seite rechts', 'Detail', 'Innenraum', 'Sonstiges'];

const MAX_FOTOS = 8;

interface CapturedFoto {
  base64: string;
  dataUrl: string;
  tag: FotoTag;
}

interface Props {
  sessionId: string;
  onComplete: (urls: string[]) => void;
  onBack: () => void;
}

type Phase = 'camera' | 'quality-warning' | 'tag-select' | 'uploading' | 'nocamera';

interface QualityWarning {
  tooDark: boolean;
  tooBlurry: boolean;
  base64: string;
  dataUrl: string;
}

const BRAND_BLUE = '#1E5FB7';
const BRAND_GREEN = '#3DB54A';

export function SchadenFotosStep({ sessionId, onComplete, onBack }: Props) {
  const [phase, setPhase] = useState<Phase>('camera');
  const [fotos, setFotos] = useState<CapturedFoto[]>([]);
  const [qualityWarning, setQualityWarning] = useState<QualityWarning | null>(null);
  const [pendingBase64, setPendingBase64] = useState<{ base64: string; dataUrl: string } | null>(null);
  const [selectedTag, setSelectedTag] = useState<FotoTag | null>(null);
  const [uploadError, setUploadError] = useState('');

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let active = true;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        });
        if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch {
        if (active) setPhase('nocamera');
      }
    }

    if (phase === 'camera') startCamera();
    return () => {
      active = false;
      if (phase !== 'camera') stopCamera();
    };
  }, [phase, stopCamera]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);

    const quality = checkImageQuality(canvas);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
    const base64 = dataUrl.replace('data:image/jpeg;base64,', '');

    if (!quality.ok) {
      setQualityWarning({ tooDark: quality.tooDark, tooBlurry: quality.tooBlurry, base64, dataUrl });
      setPhase('quality-warning');
      stopCamera();
    } else {
      setPendingBase64({ base64, dataUrl });
      setSelectedTag(null);
      setPhase('tag-select');
      stopCamera();
    }
  }, [stopCamera]);

  const acceptQualityWarning = useCallback(() => {
    if (!qualityWarning) return;
    setPendingBase64({ base64: qualityWarning.base64, dataUrl: qualityWarning.dataUrl });
    setQualityWarning(null);
    setSelectedTag(null);
    setPhase('tag-select');
  }, [qualityWarning]);

  const retakePhoto = useCallback(() => {
    setQualityWarning(null);
    setPendingBase64(null);
    setSelectedTag(null);
    setPhase('camera');
  }, []);

  const confirmTag = useCallback(() => {
    if (!pendingBase64 || !selectedTag) return;
    setFotos(prev => [...prev, { base64: pendingBase64.base64, dataUrl: pendingBase64.dataUrl, tag: selectedTag }]);
    setPendingBase64(null);
    setSelectedTag(null);
    setPhase('camera');
  }, [pendingBase64, selectedTag]);

  const deleteFoto = useCallback((idx: number) => {
    setFotos(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleWeiter = useCallback(async () => {
    if (fotos.length === 0) return;
    setPhase('uploading');
    setUploadError('');

    try {
      const res = await fetch('/api/kiosk/upload-schadenfotos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          photos: fotos.map(f => ({ image: f.base64, tag: f.tag, mimeType: 'image/jpeg' })),
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      onComplete(data.urls ?? []);
    } catch (err) {
      console.error('[SchadenFotos] Upload-Fehler:', err);
      setUploadError('Upload fehlgeschlagen. Bitte erneut versuchen.');
      setPhase('camera');
    }
  }, [fotos, sessionId, onComplete]);

  const displayedFotos = fotos.slice(0, 6);
  const canAddMore = fotos.length < MAX_FOTOS;
  const canProceed = fotos.length >= 1;

  return (
    <div data-testid="schaden-fotos-step" className="flex flex-col gap-5 w-full">
      <div className="text-center">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md"
          style={{ background: BRAND_BLUE }}
        >
          <Camera size={26} className="text-white" />
        </div>
        <h2 className="text-[22px] font-bold text-gray-900">Fotos vom Schaden</h2>
        <p className="text-[14px] text-gray-500 mt-1">
          {fotos.length} / {MAX_FOTOS} Fotos aufgenommen
        </p>
      </div>

      <AnimatePresence mode="wait">

        {/* ── Kamera-Phase ── */}
        {(phase === 'camera' || phase === 'nocamera') && (
          <motion.div
            key="camera"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-4"
          >
            {uploadError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-[13px] text-red-700">
                <AlertCircle size={15} className="flex-shrink-0" />
                {uploadError}
              </div>
            )}

            {phase === 'nocamera' ? (
              <div className="w-full bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 text-center">
                <AlertCircle size={32} className="text-amber-500 mx-auto mb-3" />
                <p className="text-[14px] font-semibold text-amber-800 mb-1">Kamerazugriff nicht möglich</p>
                <p className="text-[13px] text-amber-700">
                  Bitte erlauben Sie den Kamerazugriff in den Browser-Einstellungen.
                </p>
              </div>
            ) : (
              <div
                className="relative rounded-2xl overflow-hidden bg-black w-full shadow-xl"
                style={{ aspectRatio: '4/3', maxHeight: 300 }}
                data-testid="schaden-camera-viewfinder"
              >
                <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <span className="bg-black/60 text-white text-[12px] font-semibold px-3 py-1 rounded-full">
                    {fotos.length}/{MAX_FOTOS} Fotos
                  </span>
                </div>
              </div>
            )}

            <canvas ref={canvasRef} className="hidden" />

            {/* ── Thumbnail-Galerie ── */}
            {displayedFotos.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="foto-galerie">
                {displayedFotos.map((foto, i) => (
                  <div key={i} className="relative rounded-xl overflow-hidden aspect-square bg-gray-100 shadow-sm">
                    <img src={foto.dataUrl} alt={foto.tag} className="w-full h-full object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] font-medium px-1.5 py-0.5 truncate">
                      {foto.tag}
                    </div>
                    <button
                      data-testid={`delete-foto-${i}`}
                      onClick={() => deleteFoto(i)}
                      className="absolute top-0 right-0 w-[60px] h-[60px] flex items-end justify-end p-1"
                    >
                      <span className="w-6 h-6 bg-red-500/90 rounded-full flex items-center justify-center shadow">
                        <Trash2 size={11} className="text-white" />
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
                style={{ minHeight: 60 }}
              >
                Zurück
              </button>

              {canAddMore && (
                <button
                  data-testid="capture-button"
                  onClick={capturePhoto}
                  disabled={phase === 'nocamera'}
                  className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 60 }}
                >
                  <Camera size={18} />
                  Foto aufnehmen
                </button>
              )}

              <button
                data-testid="weiter-button"
                onClick={handleWeiter}
                disabled={!canProceed}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: canProceed ? `linear-gradient(135deg, ${BRAND_GREEN}, #2D9E3A)` : '#9CA3AF', minHeight: 60 }}
              >
                <CheckCircle2 size={18} />
                Weiter
              </button>
            </div>

            {!canProceed && (
              <p className="text-[12px] text-gray-400 text-center">
                Mindestens 1 Foto erforderlich — Fotos sind Pflicht für das Gutachten
              </p>
            )}
          </motion.div>
        )}

        {/* ── Qualitätswarnung ── */}
        {phase === 'quality-warning' && qualityWarning && (
          <motion.div
            key="quality-warning"
            data-testid="quality-warning"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="relative w-full rounded-2xl overflow-hidden shadow-md" style={{ aspectRatio: '4/3', maxHeight: 240 }}>
              <img src={qualityWarning.dataUrl} alt="Vorschau" className="w-full h-full object-cover opacity-60" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white/90 rounded-2xl px-6 py-4 text-center shadow-lg max-w-xs">
                  {qualityWarning.tooDark ? (
                    <div className="flex flex-col items-center gap-2">
                      <Sun size={28} className="text-amber-500" />
                      <p className="text-[14px] font-bold text-gray-900">Bitte mehr Licht</p>
                      <p className="text-[12px] text-gray-500">Das Foto ist zu dunkel — bitte für bessere Beleuchtung sorgen.</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Aperture size={28} className="text-blue-500" />
                      <p className="text-[14px] font-bold text-gray-900">Bitte schärfer aufnehmen</p>
                      <p className="text-[12px] text-gray-500">Das Foto ist unscharf — bitte Kamera ruhig halten.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 w-full">
              <button
                data-testid="retake-button"
                onClick={retakePhoto}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all"
                style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 60 }}
              >
                <RefreshCw size={16} />
                Erneut aufnehmen
              </button>
              <button
                data-testid="accept-anyway-button"
                onClick={acceptQualityWarning}
                className="px-5 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[13px] font-semibold text-gray-500"
                style={{ minHeight: 60 }}
              >
                Trotzdem verwenden
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Tag-Auswahl ── */}
        {phase === 'tag-select' && pendingBase64 && (
          <motion.div
            key="tag-select"
            data-testid="tag-select"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-4"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-md w-full" style={{ aspectRatio: '4/3', maxHeight: 220 }}>
              <img src={pendingBase64.dataUrl} alt="Aufgenommenes Foto" className="w-full h-full object-cover" />
            </div>

            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-2">Was zeigt dieses Foto?</p>
              <div className="flex flex-wrap gap-2" data-testid="tag-chips">
                {FOTO_TAGS.map(tag => (
                  <button
                    key={tag}
                    data-testid={`tag-${tag.replace(/\s+/g, '-').toLowerCase()}`}
                    onClick={() => setSelectedTag(tag)}
                    className="px-4 py-2 rounded-xl border-2 text-[13px] font-semibold transition-all"
                    style={{
                      minHeight: 60,
                      borderColor: selectedTag === tag ? BRAND_BLUE : '#E5E7EB',
                      background: selectedTag === tag ? '#EFF6FF' : '#F9FAFB',
                      color: selectedTag === tag ? BRAND_BLUE : '#374151',
                    }}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={retakePhoto}
                className="px-5 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
                style={{ minHeight: 60 }}
              >
                <RefreshCw size={16} className="inline mr-1" />
                Neu aufnehmen
              </button>
              <button
                data-testid="confirm-tag-button"
                onClick={confirmTag}
                disabled={!selectedTag}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: `linear-gradient(135deg, ${BRAND_GREEN}, #2D9E3A)`, minHeight: 60 }}
              >
                <CheckCircle2 size={18} />
                Foto speichern
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Upload-Phase ── */}
        {phase === 'uploading' && (
          <motion.div
            key="uploading"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-6 py-8 text-center"
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg"
              style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)` }}
            >
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                <RefreshCw size={36} className="text-white" />
              </motion.div>
            </div>
            <div>
              <h2 className="text-[22px] font-bold text-gray-900">Fotos werden hochgeladen</h2>
              <p className="text-[14px] text-gray-500 mt-1">{fotos.length} Foto{fotos.length !== 1 ? 's' : ''} werden gespeichert…</p>
            </div>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
}
